import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        logger.error('Error getting session:', error);
      }
      logger.info('Initial session:', session?.user ? 'User found' : 'No user');
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.info('Auth state changed:', event);
        setUser(session?.user ?? null);
        setIsLoading(false);
        
        if (event === 'SIGNED_OUT') {
          queryClient.clear();
          navigate('/login');
        }
        
        // Invalider les queries lors de la connexion, mais ne pas rediriger automatiquement
        if (event === 'SIGNED_IN') {
          queryClient.invalidateQueries();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient, navigate]);

  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      logger.info('User signed in successfully', data.user?.email);
      // Redirection manuelle après connexion réussie
      navigate('/dashboard');
      return { user: data.user, error: null };
    } catch (error) {
      logger.error('Sign in error:', error);
      setIsLoading(false); // Arrêter le loading en cas d'erreur
      return { user: null, error: error as Error };
    }
    // Ne pas mettre setIsLoading(false) ici car la redirection va se faire
  };

  const signOut = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      logger.info('User signed out successfully');
      queryClient.clear();
    } catch (error) {
      logger.error('Sign out error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signOut
  };
}
